<template>
  <div :class="classroom_create_class">
    <div v-if="currentStep < 3" class="classroom-step-wrapper">
      <b-list-group class="steps-list" horizontal>
        <b-list-group-item
          v-for="step in steps"
          :key="'step-' + step.num"
          class="step-item"
          :class="{ 'step-item-active': currentStep >= step.num }"
        >
          <b-icon
            v-if="currentStep >= step.num"
            icon="check2"
            class="step-item-active-icon"
            aria-hidden="true"
          ></b-icon>
        </b-list-group-item>
      </b-list-group>

      <div class="classroom-step-item classroom-step-1" v-if="currentStep === 1">
        <div class="classroom-step-title">What do you want to call this Classroom?</div>
        <form-input v-model="$v.classroom.name.$model" :validator="$v.classroom.name" />

        <b-button
          type="button"
          class="button primary --sm"
          :disabled="$v.step_1.$invalid"
          @click="nextStep(2)"
          btn-text="Next"
        />
      </div>

      <div class="classroom-step-item classroom-step-4" v-if="currentStep === 2">
        <div class="classroom-step-title classroom-step-title--second">
          How old are your students?
        </div>
        <div class="classroom-step-subtitle">Please select the approximate age range.</div>
        <form-select
          v-model="$v.classroom.age_range_id.$model"
          :validator="$v.classroom.age_range_id"
          :options="age_ranges"
          custom-class="custom-dropdown--md custom-dropdown--secondary"
        />

        <b-button
          type="button"
          class="button primary --sm back"
          @click="nextStep(1)"
          btn-text="Back"
        />

        <b-button
          type="button"
          class="button primary --sm"
          :disabled="$v.step_2.$invalid"
          @click="showModalAgreeAction()"
          btn-text="Next"
        />
      </div>

      <b-overlay no-wrap :show="creatingClassroom || loadingProfile || sendingRequest"></b-overlay>
    </div>
    <div v-else class="classroom-page-container">
      <b-row>
        <b-col cols="12">
          <h2 class="security-form-subtitle security-form-subtitle--primary mb-4">
            SELECT A COURSE FOR YOUR CLASSROOM
          </h2>
          <h2 class="security-form-subtitle security-form-subtitle--secondary mb-1">
            Click 'See Details' to learn more about each course. You may also use the filters to
            search for more options.
          </h2>
          <h2 class="security-form-subtitle security-form-subtitle--secondary mb-4">
            You may SKIP this step for now if you are open to discussing any topic or are not yet
            sure which course you want choose.
          </h2>
          <h2 class="security-form-subtitle security-form-subtitle--normal mb-1">
            {{ classroom.name }}
          </h2>
          <div class="wrapper-classroom-content">
            <courses-list
              :default_filter_by="filter_by"
              :default_search_string="search_string"
              :classroom_id="classroom.id"
              :subject_id="classroom.subject_id"
              @on-course-add="onCourseAdd"
            ></courses-list>
          </div>

          <div class="security-form-container-btns c-btns-secondary mt-0">
            <b-button @click="saveClassroom" btn-text="Skip" />
          </div>
        </b-col>
      </b-row>
    </div>

    <modal
      id="first-classroom-modal"
      modal-class="classroom_first_classroom_modal"
      :hide-close-button="true"
    >
      <template #header></template>
      <template #body>
        <h2 class="modal-title">WELCOME</h2>
        <p class="text-center">
          <b>Let’s set up your first Classroom!</b><br />
          Click next to get started.
        </p>
      </template>
      <template #footer>
        <b-button @click="$bvModal.hide('first-classroom-modal')" btn-text="NEXT" />
      </template>
    </modal>
  </div>
</template>

<script>
import FormInput from '@/core/components/Form/Input'
import FormSelect from '@/core/components/Form/Select'
import { Button } from '@/core/components/Button'
import { ClassroomMixins, UtilsMixins, userTypeMixins } from '@/core/Mixins'
import { Modal } from '@/core/components/Modal'
import { required, minLength } from 'vuelidate/lib/validators'
import CoursesList from '@/core/components/CoursesList'
import moment from 'moment'
import { userTypes, SubjectTypes } from '@/core/Utils'
import { CoursesService } from '@/core/Services'

export default {
  name: 'CreateIndependentClassroomPage',
  components: {
    Modal,
    'b-button': Button,
    'form-input': FormInput,
    'form-select': FormSelect,
    CoursesList
  },
  mixins: [UtilsMixins, ClassroomMixins, userTypeMixins],
  data() {
    return {
      currentStep: 1,
      minStartDate: new Date(),
      skipCourseContent: false,
      filter_by: null,
      search_string: null,
      buttonAction: null,
      sendingRequest: false
    }
  },
  props: {
    invitation_id: {
      type: Number,
      required: false
    }
  },
  validations() {
    return {
      classroom: {
        name: {
          required,
          minLength: minLength(3)
        },
        age_range_id: {
          required
        }
      },
      step_1: ['classroom.name'],
      step_2: ['classroom.age_range_id']
    }
  },
  mounted() {
    this.initClassroom()
    this.init()
  },
  methods: {
    async init() {
      this.loading = true

      if (!this.classrooms.length) {
        await this.getClassrooms({})

        if (!this.classrooms.length) {
          this.$bvModal.show('first-classroom-modal')
        }
      }

      const setSubjectId = () => {
        if (this.user.user_type.slug === userTypes.independentTeacherBasic) {
          return SubjectTypes.independentBasic.id
        }
        if (this.user.user_type.slug === userTypes.independentTeacherLanguage) {
          return SubjectTypes.independentLanguage.id
        }
        if (this.user.user_type.slug === userTypes.independentTeacherPlayers) {
          return SubjectTypes.independentPlayersTrust.id
        }
      }
      this.classroom.subject_id = setSubjectId() || null

      if (this.user.age_ranges.length) {
        const ages = ['15+ years old', '11-14 years old', '7-10 years old', '4-6 years old']

        for (let i = 0; i < ages.length; i++) {
          let range = this.user.age_ranges.find((range) => range.name === ages[i])

          if (range) {
            this.classroom.age_range_id = range.id
            break
          }
        }
      }

      if (!this.classroom.age_range_id) {
        let range = this.age_ranges.find((range) => range.name === '15+ years old')

        if (range) {
          this.classroom.age_range_id = range.id
        }
      }

      this.loading = false
    },
    onCourseAdd(selectedCourse) {
      this.setCourseId(selectedCourse.id)

      this.saveClassroom()
    },
    async saveClassroom() {
      this.$v.classroom.$touch()
      if (this.$v.classroom.$anyError) {
        return
      }

      if (this.sendingRequest) {
        return
      }

      this.sendingRequest = true

      const currentTimestamp = moment()

      let data = {
        name: this.classroom.name,
        subject_id: this.classroom.subject_id,
        age_range_id: this.classroom.age_range_id,
        number_students_range_id: this.classroom.number_students_range_id,
        lang_instruction_id: this.classroom.lang_instruction_id,
        lang_native_id: this.classroom.lang_native_id,
        start_date: currentTimestamp.format('YYYY-MM-DD'),
        end_date: currentTimestamp.add(90, 'days').format('YYYY-MM-DD'),
        level_id: this.classroom.level_id
      }

      if (this.classroom.course_id) {
        data.course_id = this.classroom.course_id
      }

      const getCoursesBySubject = (subjectId) => {
        return CoursesService.list({ subject_id: subjectId })
      }

      let responseCourses
      if (this.isIndependentTeacherBasic) {
        responseCourses = await getCoursesBySubject(SubjectTypes.independentBasic.id)
      }
      if (this.isIndependentTeacherLanguage) {
        responseCourses = await getCoursesBySubject(SubjectTypes.independentLanguage.id)
      }
      if (responseCourses?.data?.length) {
        data.course_id = responseCourses.data[0].id
      }

      try {
        const response = await this.createClassroom(data)

        if (response.success === true) {
          this.$bvModal.show('classroom-create-success-modal')
          //go to global partner with the search engine displayed
          await this.goToClassroomPage(response.data.id, { collapseInformation: 'true' })
        } else {
          this.$bvModal.msgBoxOk(`${response.message}, Please try again.`, {
            size: 'sm',
            dialogClass: 'modal-message-box',
            noCloseOnBackdrop: true,
            centered: true
          })
        }
      } catch (e) {
        const errorMessage = e.message ? e.message : 'Error fetching connection request'

        this.$bvModal.msgBoxOk(`${errorMessage}, Please try again.`, {
          size: 'sm',
          dialogClass: 'modal-message-box',
          noCloseOnBackdrop: true,
          centered: true
        })
      } finally {
        this.sendingRequest = false
      }
    },
    nextStep(next) {
      this.currentStep = next
    },
    ageAlertMessage() {
      return this.$bvModal.msgBoxOk(
        `Please note that you will be required to review and approve each video from your students before they will be shared.`,
        {
          size: 'sm',
          dialogClass: 'modal-message-box',
          noCloseOnBackdrop: true,
          centered: true
        }
      )
    },
    selectCourseContent() {
      this.filter_by = 'all'
      this.search_string = ''
      this.currentStep = 3
    },
    handleModalAgreeAction() {
      if (this.selectedAge.min_age < 13 && this.currentStep === 2) {
        this.ageAlertMessage().then(() => {
          this.handleActionSelected()
        })
        return
      }
      this.handleActionSelected()
    },
    handleActionSelected() {
      if (this.isIndependentTeacherBasic || this.isIndependentTeacherLanguage) {
        this.saveClassroom()
      } else {
        this.selectCourseContent()
      }
    },
    showModalAgreeAction() {
      if (!this.user.teacher_agreement_completed_at) {
        this.$bvModal.show('teacher-agreement-modal')
      } else {
        this.handleModalAgreeAction()
      }
    }
  },
  computed: {
    steps() {
      let steps = [{ num: 1 }, { num: 2 }]

      return steps
    },
    classroom_create_class() {
      return {
        'wrapper-create-classroom-page': this.currentStep < 3,
        'wrapper-classroom-page': this.currentStep === 3
      }
    },
    selectedAge() {
      return this.age_ranges.find((val) => val.id === this.classroom.age_range_id)
    }
  },
  watch: {
    '$route.params.classroomId': {
      handler: function (classroomId) {
        if (!classroomId) {
          return
        }

        if (typeof classroomId === 'string') {
          this.classroomId = parseInt(classroomId)
        } else {
          this.classroomId = classroomId
        }

        this.getClassroom(this.classroomId).then((response) => {
          if (!response.success) {
            if (this.$route.query['partner-invitation']) {
              this.$EventBus.$emit('on-wrong-teacher-partner-invitation', response)
            } else {
              this.$EventBus.$emit('on-classroom-error', response)
            }
          }
        })
      },
      deep: true,
      immediate: true
    }
  }
}
</script>

<style lang="stylus" scoped>
@import "~@/core/styles/variables.styl"
@import "~@/core/styles/mixins.styl"

.wrapper-create-classroom-page
  min-height inherit
  display flex
  align-items center

.classroom-step
  &-wrapper
    max-width 875px
    min-width 320px
    width 100%
    margin 0 auto
    padding 25px

    .form-group
      max-width 470px
      margin 0 auto

    @media (max-width 414px)
      padding 2rem
      min-height: calc(100vh - (80px + 115px));

  &-item
    text-align center

    .button
      display inline
      margin-top 2rem

      &.back
        margin-right 4rem

      &:disabled
        border-width 0 !important

    @media (max-width 350px)
      .button
        margin 0 5px
        margin-top 2rem

        &.back
          margin-right 0

  &-title
    font-size 1.5rem
    margin-bottom 18px

  &-title--second
    margin-top 18px

  &-subtitle
    margin 18px 0
    font-size 1rem

.steps-list
  margin-bottom 2rem

  .step-item
    background-color color-4
    margin-right 2rem
    border-radius 50% !important
    height 2rem
    width 2rem
    padding 0
    border 2px solid color-7 !important
    text-align center

    &:not(:last-child):before
      content ''
      border-bottom .1rem solid color-3
      position absolute
      transform translateY(-50%)
      top 50%
      right -1.5rem
      width 1rem

    &-active
      background-color color-5

      &-icon
        font-size 1.5rem
        height 100%


.button-agreement
  padding-right 77px
  padding-left 77px

.modal-title--agreement
  text-transform capitalize

.security-form-subtitle--primary
  font-size 16px

.security-form-subtitle--secondary
  font-size 14px
  padding 0 20px
  text-transform none

::v-deep
  .modal-message-box
    margin 0 auto !important
</style>

<style lang="stylus">
@import "~@/core/styles/variables.styl"
@import "~@/core/styles/mixins.styl"

.first-classroom-modal
  .modal-dialog
    .modal-body
      max-width: 506px
      margin 21px auto

#teacher-agreement-modal
  overflow-y scroll !important
  overflow-x visible !important

  .modal-dialog
    max-width 700px
    min-height auto !important
    max-height none !important
    height auto !important
    margin 5.5rem auto !important

    .modal-body
      overflow-y visible !important
      font-size 14px

      .modal-list-agreement
        li
          margin-bottom 6px
</style>
